import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InputField from "components/Site/Common/Form/ReactHookForm/InputField";
import DateInput from "components/Site/Common/Form/ReactHookForm/DateInput";
import CheckboxWithLabel from "components/Site/Common/Form/ReactHookForm/CheckboxWithLabel";
import SelectField from "components/Site/Common/Form/ReactHookForm/SelectField";
import { useModal } from "src/hooks/useModal";
import { formatPhoneNumber } from "src/utils/common";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { Dispatch, SetStateAction, useMemo } from "react";
import dayjs from "dayjs";
import RightDetailBox from "../_RightDetailBox";
import { createServicePromotionAskV2 } from "src/service/pay";
import { notifyError, notifySuccess } from "src/components/Common/Toast";
import { useRouter } from "src/hooks/useRouter";
import { useTranslation } from "react-i18next";
// import { IssueAppType } from "src/service/types/pay.type";

type IssueAppType = {
  appName: string;
  appVersion: string;
};

// 기본 스키마 타입
type BaseFormValues = {
  companyName: string;
  startDate: string;
  endDate: string;
  licenseCount: number;
  product: string;
  purpose?: string;
  telNumber: string;
  userName: string;
  confirmPolicy: boolean;
};

// FIRESIDE 제품 스키마 타입
type FiresideFormValues = BaseFormValues & {
  packageName: string;
};

// 전체 폼 타입
type FormValues = BaseFormValues & {
  packageName?: string;
  externalLicenseCount?: number;
  userPerStorage?: number;
  issueAppList?: IssueAppType[];
};

interface IAskPromotionProps {
  setToggleAskPromotionOn: Dispatch<SetStateAction<boolean>>;
}

const AskPromotionNew = ({ setToggleAskPromotionOn }: IAskPromotionProps) => {
  const route = useRouter();
  const currentProduct = getProductFromSessionStorage();
  const { t } = useTranslation();
  // 동적 스키마 생성
  const schema = useMemo(() => {
    const baseSchema = yup.object().shape({
      companyName: yup
        .string()
        .required("admin.common.alert.check.enterCompany"),
      startDate: yup.string().required(),
      endDate: yup.string().required(),
      licenseCount: yup.number().required(),
      product: yup.string().required(),
      purpose: yup.string().optional(),
      telNumber: yup.string().required("siteType.free.detail.alertMsg5"),
      userName: yup
        .string()
        .required("logIn.enterName")
        .max(50, "siteType.free.detail.alertMsg1"),
      confirmPolicy: yup
        .boolean()
        .oneOf([true], "logIn.signUp.agreement")
        .required(),
    });

    // DSP 제품일 경우 추가 필드
    if (currentProduct === "DSP") {
      return baseSchema.shape({
        packageName: yup.string().required("패키지를 선택해주세요."),
      });
    }

    return baseSchema;
  }, [currentProduct]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    defaultValues: {
      product: currentProduct,
      licenseCount: 10,
      userPerStorage: 50,
      startDate: dayjs().format("YYYY-MM-DD"),
      endDate: dayjs().add(1, "year").format("YYYY-MM-DD"),
      // packageName: currentProduct === "DSP" ? "DOCUMENT_SECURITY" : undefined,
    },
    resolver: yupResolver(schema),
    mode: "onChange",
    criteriaMode: "all", // 모든 유효성 검사 규칙을 확인
  });

  const onSubmit = async (data: FormValues) => {
    // console.log({
    //   product: data?.product,
    //   userName: data.userName,
    //   telNumber: formatPhoneNumber(data.telNumber),
    //   companyName: data.companyName,
    //   purpose: data?.purpose ?? "",
    //   licenseCount: data?.licenseCount,
    //   startDate: data?.startDate ?? "",
    //   endDate: data?.endDate ?? "",
    //   userPerStorage: data?.userPerStorage,
    // });
    await createServicePromotionAskV2({
      product: data?.product,
      userName: data.userName,
      telNumber: formatPhoneNumber(data.telNumber),
      companyName: data.companyName,
      purpose: data?.purpose ?? "",
      licenseCount: data?.licenseCount,
      userPerStorage: data?.userPerStorage,
      startDate: data?.startDate ?? "",
      endDate: data?.endDate ?? "",
    })
      .then((result) => {
        notifySuccess(t("siteType.promotionApply.title"));
        setToggleAskPromotionOn((prev) => !prev);
        route.goSitePromotion({});
      })
      .catch((error: any) => {
        if (error?.errorCode && error?.errorMessage) {
          notifyError(error.errorMessage);
        }
      });
  };

  const { openTermsService, openPrivatePolicy, openPaidTermsService } =
    useModal();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form">
      <div className="flex justify-space-between">
        <div className="flex-item box box__lt bg-white">
          <h4 className="form-title">{t("siteType.free.detail.title")}</h4>
          <InputField
            register={register}
            name="userName"
            label={t("siteType.free.detail.username")}
            labelRight={
              <span
                className="form-item-question cursor-pointer"
                onClick={() => setToggleAskPromotionOn((prev) => !prev)}
              >
                {t("siteType.premium.detail.plan.haveCode")}
              </span>
            }
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="telNumber"
            label={t("siteType.free.detail.phoneNumber")}
            onChange={(e) => {
              e.target.value = formatPhoneNumber(e.target.value);
            }}
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="companyName"
            label={t("siteType.free.detail.company")}
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="purpose"
            label={t("siteType.free.detail.purpose")}
            errors={errors}
            type="textarea"
          />
          <h4 className="form-title">
            {t("siteType.premium.detail.plan.title")}
          </h4>

          {/* FIRESIDE 제품일 때만 패키지 선택 필드 표시 */}
          {currentProduct === "DSP" && (
            <SelectField
              label="Package"
              register={register}
              required={true}
              name="packageName"
              options={[
                {
                  label: t("siteType.premium.detail.plan.package.dsp_1"),
                  value: "DOCUMENT_SECURITY",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_2"),
                  value: "PRINT_SECURITY",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_3"),
                  value: "SCREEN_SECURITY",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_4"),
                  value: "DOCUMENT_DR",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_5"),
                  value: "DOCUMENT_SECURITY_PLUS_PACK",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_6"),
                  value: "DOCUMENT_SECURITY_DR_PACK",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_7"),
                  value: "PRIVACY_PACK",
                },
                {
                  label: t("siteType.premium.detail.plan.package.dsp_8"),
                  value: "ALL_IN_ONE_DATA_SECURITY_PACK",
                },
              ]}
            />
          )}

          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.basic.detail.plan.userNum")}
            </span>
            <input
              {...register("licenseCount")}
              type="number"
              className="form-control custom-form width max-w-110"
              min={10}
              max={9999}
            />
          </div>
          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.premium.detail.plan.userStorage")}
            </span>

            <input
              {...register("userPerStorage")}
              type="number"
              className="form-control custom-form width max-w-110"
              min={1}
              max={99999999}
            />
          </div>
          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.premium.detail.plan.period")}
            </span>
            <div className="flex align-bottom input_date">
              <DateInput
                control={control}
                name="startDate"
                label={t("siteType.premium.detail.plan.startDate")}
              />
              <DateInput
                control={control}
                name="endDate"
                label={t("siteType.premium.detail.plan.endDate")}
              />
            </div>
          </div>
          <CheckboxWithLabel
            id="confirmPolicy"
            name="confirmPolicy"
            register={register}
            errors={errors}
            labelContent={
              <>
                {t("siteType.agreement1")}
                <a onClick={openTermsService} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement2")}</b>
                </a>
                {t("siteType.agreement3")}
                <a onClick={openPrivatePolicy} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement4")}</b>
                </a>
                {currentProduct === "WRAPSODY_ECO" && (
                  <>
                    {t("siteType.agreement5")}
                    <a
                      onClick={openPaidTermsService}
                      className="cursor-pointer"
                    >
                      <b className="text text--black">
                        {t("siteType.agreement6")}
                      </b>
                    </a>
                  </>
                )}
                {t("siteType.agreement7")}
              </>
            }
          />
        </div>
        <RightDetailBox
          toggleAskPromotionOn={true}
          licenseCount={watch("licenseCount") || 0}
          startDate={watch("startDate") || ""}
          endDate={watch("endDate") || ""}
          isCheckAskPromotion={isValid}
        />
      </div>
      {/* <button
        type="submit"
        className="bg-green-500 text-white px-4 py-2 rounded"
      >
        제출
      </button> */}
    </form>
  );
};

export default AskPromotionNew;
